import { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
  titleClassName?: string;
}

export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide,
  titleClassName = 'font-display text-xl text-navy',
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className={`m-auto p-0 w-[calc(100%-2rem)] ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] rounded-2xl bg-white text-foreground shadow-2xl overflow-hidden animate-fade-in backdrop:bg-black/40 open:flex flex-col`}
    >
      <div className="flex items-center justify-between gap-3 px-6 py-5 border-b border-border">
        <div className="min-w-0">
          <h2 id={titleId} className={titleClassName}>
            {title}
          </h2>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Lukk"
          className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-background transition-colors cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="min-h-0 flex-auto overflow-y-auto px-6 py-5">{children}</div>
      {footer && <div className="px-6 py-4 border-t border-border flex justify-end gap-2">{footer}</div>}
    </dialog>
  );
}
